/**
 * analytics.js
 * Lightweight event tracking utility.
 * Works with Google Analytics 4 (gtag) and Meta Pixel (fbq).
 * 
 * Usage:
 *   import { track } from '../lib/analytics';
 *   track('cta_click', { button: 'Order via WhatsApp', page: 'homepage' });
 */

const IS_PROD = process.env.NODE_ENV === 'production';

/**
 * Fire a custom tracking event.
 * @param {string} eventName  - e.g. 'cta_click', 'whatsapp_click', 'product_view'
 * @param {object} params     - additional metadata
 */
export const track = (eventName, params = {}) => {
  const payload = {
    ...params,
    timestamp: new Date().toISOString(),
    page: window.location.pathname,
  };

  if (!IS_PROD) {
    console.log('[Analytics]', eventName, payload);
  }

  // Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, payload);
  }

  // Meta Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', eventName, payload);
  }
};

/**
 * Standard event helpers
 */
export const trackCTAClick = (buttonText, location) =>
  track('cta_click', { button_text: buttonText, location });

export const trackWhatsAppClick = (location) =>
  track('whatsapp_click', { location, platform: 'whatsapp' });

export const trackInstagramClick = (location) =>
  track('instagram_click', { location, platform: 'instagram' });

export const trackProductView = (productId, productName) =>
  track('product_view', { product_id: productId, product_name: productName });

export const trackPageView = (pageName) =>
  track('page_view', { page_name: pageName });

/**
 * Inject GA4 script (call once in index.html or App.js)
 * @param {string} measurementId - e.g. 'G-XXXXXXXXXX'
 */
export const initGA4 = (measurementId) => {
  if (!measurementId || typeof document === 'undefined') return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);
};

/**
 * Inject Meta Pixel (call once)
 * @param {string} pixelId - e.g. '123456789'
 */
export const initMetaPixel = (pixelId) => {
  if (!pixelId || typeof window === 'undefined') return;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
  document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
};
